import type { BookingResponse } from "@/api/types";
import { formatTimeInZone, wallClockToFormFields } from "@/lib/business-time";

export function hasBookingNotes(notes: string | null | undefined): notes is string {
  return typeof notes === "string" && notes.trim().length > 0;
}

function isBookingPast(scheduledAt: string): boolean {
  return new Date(scheduledAt) < new Date();
}

export function isBookingUpcoming(booking: { status: string; scheduled_at: string }): boolean {
  return booking.status === "confirmed" && !isBookingPast(booking.scheduled_at);
}

export function canBookAgain(booking: BookingResponse): boolean {
  const repeatable =
    booking.status === "completed" ||
    booking.status === "noshow" ||
    booking.status === "cancelled" ||
    ((booking.status === "confirmed" || booking.status === "arrived") &&
      isBookingPast(booking.scheduled_at));
  return !!(
    repeatable &&
    booking.business.booking_enabled &&
    booking.business.timezone &&
    booking.business_service?.id
  );
}

export function deriveBookings(
  upcoming: BookingResponse[],
  past: BookingResponse[],
): {
  upcoming: BookingResponse[];
  past: BookingResponse[];
  cancelled: BookingResponse[];
} {
  const all = [...upcoming, ...past];
  return {
    upcoming: upcoming.filter((b) => b.status !== "cancelled"),
    past: past.filter((b) => b.status !== "cancelled"),
    cancelled: all.filter((b) => b.status === "cancelled"),
  };
}

/** Matches `/bookings` Upcoming tab count (excludes cancelled in API upcoming bucket). */
export function getUpcomingBookingsCount(
  upcoming: BookingResponse[],
  past: BookingResponse[],
): number {
  return deriveBookings(upcoming, past).upcoming.length;
}

export function formatBookingsNavLabel(count: number): string {
  return count > 0 ? `Bookings, ${count} upcoming` : "Bookings";
}

export function formatPrice(cents: number, currency: string): string {
  return new Intl.NumberFormat("en", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

export function formatShortDate(iso: string, timeZone: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone,
  });
}

export function formatDateTime(iso: string, timeZone: string): string {
  return `${formatShortDate(iso, timeZone)} · ${formatTimeInZone(iso, timeZone)}`;
}

/** When-row date: Today / Tomorrow on matching calendar days, otherwise short date. */
export function formatWhenDateLabel(iso: string, timeZone: string): string {
  const countdown = getBookingCountdown(iso, timeZone);
  if (countdown === "Today" || countdown === "Tomorrow") return countdown;
  return formatShortDate(iso, timeZone);
}

export function formatLongDate(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "short",
  });
}

/** Calendar-day countdown for upcoming appointments (Today / Tomorrow / in N days). */
export function getBookingCountdown(iso: string, timeZone: string): string | null {
  const now = new Date();
  const target = new Date(iso);
  if (target.getTime() < now.getTime()) return null;

  const todayDate = wallClockToFormFields(now.toISOString(), timeZone).date;
  const targetDate = wallClockToFormFields(iso, timeZone).date;
  const days = Math.round(
    (Date.parse(`${targetDate}T00:00:00Z`) - Date.parse(`${todayDate}T00:00:00Z`)) / 86_400_000,
  );

  if (days <= 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `in ${days} days`;
}
